import {ScrollArea} from '@/base/ui/scrollArea'
import {cn} from '@/lib/utils'
import React, {useCallback, useEffect, useState} from 'react'
import {WuButton} from '../button'
import {WuMenu, WuMenuItem} from '../menu'
import {WuVirtualScroll} from '../virtualScroll'
import './WuPagination.css'

export interface IWuPaginationProps
  extends React.HTMLAttributes<HTMLDivElement> {
  totalRows: number
  initialPage: number
  initialPageSize: number
  onPageChange?: (page: number) => void
  disabled?: boolean
}

export const WuPagination = React.forwardRef<
  HTMLDivElement,
  IWuPaginationProps
>(
  (
    {
      totalRows,
      initialPage,
      initialPageSize = 100,
      onPageChange,
      className,
      disabled = false,
      ...rest
    },
    _,
  ) => {
    const [currentPage, setCurrentPage] = useState(initialPage)
    const [pageSize, setPageSize] = useState(initialPageSize)

    useEffect(() => {
      setCurrentPage(initialPage)
      setPageSize(initialPageSize)
    }, [initialPage, initialPageSize])

    const totalPages = Math.ceil(totalRows / pageSize)
    const start = currentPage * pageSize + 1
    const end = Math.min((currentPage + 1) * pageSize, totalRows)
    const pageOptions = Array.from({length: totalPages}, (_, i) => i)
    const changePage = useCallback(
      (page: number) => {
        if (page >= 0 && page < totalPages) {
          setCurrentPage(page)
          onPageChange?.(page)
        }
      },
      [totalPages, onPageChange],
    )

    const maxHeight = 7 * 32

    return (
      <div
        className={cn('flex items-center gap-[2px]', className)}
        {...rest}
        role="navigation"
      >
        <PaginationButton
          label="Previous page"
          onClick={() => changePage(currentPage - 1)}
          disabled={currentPage === 0 || disabled}
          icon="wm-arrow-back-ios-new"
          className="wu-prev-btn hover:bg-gray-paginationBg"
        />

        <WuMenu
          name={`${start} - ${end}`}
          className="w-[96px] min-w-[96px]"
          align="end"
          disabled={disabled}
        >
          <ScrollArea style={{maxHeight: maxHeight + 'px'}}>
            {pageOptions.length > 100 ? (
              <WuVirtualScroll
                className="wu-combobox-list"
                containerHeight={maxHeight}
                list={pageOptions}
                callback={(i, option) => (
                  <WuMenuItem
                    key={i}
                    onSelect={() => changePage(option as number)}
                    className={cn(
                      'py-2 hover:bg-gray-paginationBg',
                      option === currentPage && 'bg-gray-paginationBg',
                    )}
                  >
                    {(option as number) * pageSize + 1} -{' '}
                    {Math.min(((option as number) + 1) * pageSize, totalRows)}
                  </WuMenuItem>
                )}
              />
            ) : (
              pageOptions.map(page => (
                <WuMenuItem
                  key={page}
                  onSelect={() => changePage(page)}
                  className={cn(
                    page === currentPage && 'bg-gray-paginationBg',
                    'py-2 hover:bg-gray-paginationBg',
                  )}
                >
                  {page * pageSize + 1} -{' '}
                  {Math.min((page + 1) * pageSize, totalRows)}
                </WuMenuItem>
              ))
            )}
          </ScrollArea>
        </WuMenu>

        <PaginationButton
          label="Next page"
          onClick={() => changePage(currentPage + 1)}
          disabled={currentPage === totalPages - 1 || disabled}
          icon="wm-arrow-forward-ios"
          className="wu-next-btn hover:bg-gray-paginationBg"
        />
      </div>
    )
  },
)

WuPagination.displayName = 'WuPagination'

interface IPaginationButtonProps {
  label: string
  onClick: () => void
  disabled: boolean
  icon: string
  className?: string
}

const PaginationButton: React.FC<IPaginationButtonProps> = ({
  label,
  onClick,
  disabled,
  icon,
  className,
}) => (
  <WuButton
    variant="iconOnly"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'w-8 h-8 rounded-xs group disabled:text-zinc-300 disabled:bg-white text-gray-lead focus:bg-color-none',
      className,
    )}
    aria-label={label}
  >
    <span className={cn('text-sm', icon)} />
  </WuButton>
)
